<template>
    <div>
        <limitless-page-header :loading="busy_loading" :title="'Inventering'" :title-sub="stocktaking_name">
            <template #header-elements>
                <limitless-page-header-button :url="'/stocktaking/' + id" icon="icon-circle-left2">Tillbaka</limitless-page-header-button>
            </template>
        </limitless-page-header>

        <div class="content">
            <tb-skeleton v-if="busy_loading" :height="100" :theme="'opacity'" :shape="'radius'" bg-color="#dcdbdc"></tb-skeleton>

            <div v-else-if="!busy_loading && stocktaking && product">
                <div class="card">
                    <div class="card-body">
                        <div class="row">
                            <div class="col-lg-8">
                                <div>Inventering: {{ stocktaking.description }}</div>
                                <div>Datum: {{ stocktaking.start_date }}</div>
                                <div>Produkt: <router-link :to="'/products/'+ product.ProdID">#{{ product.ProdID }} - {{ product.ProdName }} {{ product.ProdColor }} {{ product.ProdSize }}</router-link></div>
                            </div>
                            <div v-if="product.images && product.images[0] && product.images[0].internal_urls" class="col-lg-4">
                                <img :src="product.images[0].internal_urls.medium" style="width: 100%;" :alt="product.ProdName">
                            </div>
                        </div>
                    </div>
                </div>

                <rednax-datatable :data="items" :columns="items_columns" sort-by="id" sort-order="desc" :row-class="(row) => row.corr ? 'bg-warning-200' : ''">
                    <div class="text-right">
                        <button type="button" class="btn btn-light" @click="showModalCorr">Lägg till</button>
                    </div>
                    <template #tfoot>
                        <tr>
                            <th></th>
                            <th></th>
                            <th></th>
                            <th></th>
                            <th>Summa:</th>
                            <th class="text-right">{{ items_sum }}</th>
                            <th class="text-right"></th>
                        </tr>
                    </template>
                </rednax-datatable>
            </div>

            <div v-else>
                <i class="text-danger">Data kunde inte hämtas...</i>
            </div>
        </div> <!-- /content -->

        <b-modal id="modal-add-corr" title="Lägg till" @ok="saveCorr" v-if="this.stocktaking">
            <ValidationObserver ref="approveform">
                <div class="form-group row">
                    <label class="col-form-label col-lg-2">Antal</label>
                    <div class="col-lg-10">
                        <input-with-validation
                            vid="count"
                            label="Antal"
                            rules="required"
                            v-model="corr.count"
                            type="number"
                            hide-label
                        />
                    </div>
                </div>
                <div class="form-group row">
                    <label class="col-form-label col-lg-2">Plats</label>
                    <div class="col-lg-10">
                        <select-with-validation
                            vid="location_id"
                            label="Plats"
                            component="stock-location-filter"
                            v-model="corr.location_id"
                            :reduce="(opt) => opt.id"
                            hide-label
                        />
                    </div>
                </div>
                <div class="form-group row">
                    <label class="col-form-label col-lg-2">Kommentar</label>
                    <div class="col-lg-10">
                        <textarea-with-validation
                            vid="comment"
                            label="Kommentar"
                            :rules="{ required: this.stocktaking.status_id === 100 || corr.corr }"
                            v-model="corr.comment"
                            :min-height="116"
                            hide-label
                        />
                    </div>
                </div>
                <div class="form-group row">
                    <label class="col-form-label col-lg-2">Korr</label>
                    <div class="col-lg-10 pt-1">
                        <div class="custom-control custom-radio custom-control-inline">
                            <input type="radio" class="custom-control-input" name="rdo-corr" id="rdo-corr-yes" :value="true" v-model="corr.corr">
                            <label class="custom-control-label" for="rdo-corr-yes">Ja</label>
                        </div>
                        <div class="custom-control custom-radio custom-control-inline">
                            <input type="radio" class="custom-control-input" name="rdo-corr" id="rdo-corr-no" :value="false" v-model="corr.corr">
                            <label class="custom-control-label" for="rdo-corr-no">Nej</label>
                        </div>
                    </div>
                </div>
            </ValidationObserver>
            <template #modal-footer="{ cancel, ok }">
                <button type="button" class="btn btn-light" @click="cancel">Avbryt</button>
                <limitless-button ref="btnaddcorr" type="button" @click="ok" :busy="busy_adding">Spara</limitless-button>
            </template>
        </b-modal>
    </div>
</template>

<script>
import apiClient from '@services/ApiClient';
import LimitlessPageHeader from '@components/Layout/partials/LimitlessPageHeader';
import LimitlessPageHeaderButton from '@components/Layout/partials/LimitlessPageHeaderButton';
import LimitlessButton from '@components/Limitless/LimitlessButton';
import RednaxDatatable from '@components/Datatable/RednaxDatatableComponent.vue';
import { formatDateTime } from '@services/Helpers';
import InputWithValidation from '@components/Form/InputWithValidation.vue';
import SelectWithValidation from '@components/Form/SelectWithValidation.vue';
import TextareaWithValidation from '@components/Form/TextareaWithValidation.vue';

export default {
    name: 'StocktakingItem',
    components: {
        TextareaWithValidation,
        SelectWithValidation,
        InputWithValidation,
        RednaxDatatable,
        LimitlessButton,
        LimitlessPageHeader,
        LimitlessPageHeaderButton,
    },
    props: {
        id: {
            type: Number,
            required: true,
        },
        sku: {
            type: Number,
            required: true,
        },
    },
    data: () => ({
        busy_loading: true,
        busy_adding: false,
        stocktaking: null,
        product: null,
        items: [],
        corr: {
            count: null,
            comment: '',
            location_id: null,
            corr: true,
        },
        statuses: [
            {
                id: 0,
                label: 'Kommande',
            },
            {
                id: 10,
                label: 'Aktiv',
            },
            {
                id: 100,
                label: 'Avslutad',
            },
        ],
        items_columns: [
            {
                header: 'SKU',
                data: 'sku_sht',
                sortable: false,
            },
            {
                header: 'Användare',
                data: 'user.FullName',
                sortable: false,
                render(row) {
                    return row.user?.FullName;
                },
            },
            {
                header: 'Kommentar',
                data: 'comment',
                sortable: false,
            },
            {
                header: 'Klockslag',
                data: 'created_at',
                sortable: false,
                render(row) {
                    return formatDateTime(row.created_at);
                },
            },
            {
                header: 'Plats',
                data: 'location.name',
                sortable: false,
                render(row) {
                    return row.location?.name;
                },
            },
            {
                header: 'Antal',
                data: 'count',
                sortable: false,
                align_right: true,
            },
            {
                header: 'Korr',
                data: 'corr',
                sortable: false,
                align_right: true,
                render(row) {
                    return row.corr ? 'Ja' : 'Nej';
                },
            },
        ],
    }),
    mounted() {
        this.init();
    },
    watch: {
        id() {
            this.init();
        },
        sku() {
            this.init();
        },
    },
    methods: {
        init() {
            this.getData();
        },
        getData() {
            apiClient.get('/vue/stocktaking/' + this.id + '/sku/' + this.sku)
                .then((response) => {
                    this.stocktaking = response.data.stocktaking;
                    this.product = response.data.product;
                    this.items = response.data.items;
                })
                .finally(() => {
                    this.busy_loading = false;
                });
        },
        async saveCorr(bvModalEvent) {
            bvModalEvent.preventDefault();
            const isValid = await this.$refs.approveform.validate();
            if (isValid) {
                this.busy_adding = true;
                await apiClient.post('/vue/store/stocktaking/' + this.id + '/sku/' + this.sku, this.corr)
                    .then(() => {
                        this.$swal({
                            icon: 'success',
                            title: 'Wohoo!',
                            text: 'Sparad!',
                            confirmButtonText: 'OK',
                            cancelButtonText: 'Avbryt',
                        });
                        this.$refs.btnaddcorr.triggerSaved();
                        this.$nextTick(() => {
                            this.$bvModal.hide('modal-add-corr');
                        });
                        this.getData();
                    })
                    .catch((error) => {
                        let message = 'Något gick fel...';
                        if (error.response?.data?.message) {
                            message = error.response.data.message;
                        }
                        this.$swal({
                            icon: 'error',
                            title: 'Error!',
                            text: message,
                            confirmButtonText: 'OK',
                            cancelButtonText: 'Avbryt',
                        });
                        this.$refs.btnaddcorr.triggerFailed();
                    })
                    .finally(() => {
                        this.busy_adding = false;
                    });
            }
        },
        showModalCorr() {
            this.corr.count = 1;
            this.corr.location_id = null;
            this.corr.comment = '';
            this.corr.corr = this.stocktaking.status_id === 100;
            this.$bvModal.show('modal-add-corr');
        },
    },
    computed: {
        stocktaking_name() {
            return (this.stocktaking && this.stocktaking.id > 0 ? this.stocktaking.description + ' #' + this.stocktaking.id : '')
                + (this.product ? ' - SKU: ' + this.product.ProdID : '');
        },
        items_sum() {
            let sum = 0;
            this.items.forEach((item) => {
                sum += item.count;
            });

            return sum;
        },
    },
};
</script>
